import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  dashApi, type Scenario, type ScenarioAssumption, type ScenarioOutputs,
} from "@/lib/dashApi";
import { EmptyState, ErrorState, LoadingState, PageHeader, Panel, StatusPill, Stat } from "@/components/dash/ui";

const TYPES = [
  { key: "sales_target_recovery", label: "Sales target recovery" },
  { key: "settled_renewal_recovery", label: "Settled renewal recovery" },
  { key: "repair_pipeline_pressure", label: "Repair pipeline pressure" },
  { key: "support_capacity", label: "Support capacity / SLA" },
  { key: "action_completion", label: "Action completion" },
];
const typeLabel = (t: string) => TYPES.find((x) => x.key === t)?.label ?? t;

function TrustLabel({ trust }: { trust: string | null }) {
  const map: Record<string, string> = {
    fresh: "text-emerald-300", stale: "text-amber-300", unknown: "text-slate-400",
    partial: "text-amber-300", estimated: "text-amber-300",
  };
  return <span className={`text-[11px] ${map[trust ?? "unknown"] ?? "text-slate-400"}`}>baseline: {trust ?? "unknown"}</span>;
}

export default function ScenarioPlanningPage() {
  const qc = useQueryClient();
  const summary = useQuery({ queryKey: ["scenario-summary"], queryFn: dashApi.scenariosSummary });
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const list = useQuery({
    queryKey: ["scenarios", statusFilter, typeFilter],
    queryFn: () => dashApi.scenarios({ status: statusFilter || undefined, scenario_type: typeFilter || undefined }),
  });
  const [compareIds, setCompareIds] = useState<number[]>([]);

  const [newTitle, setNewTitle] = useState("");
  const [newType, setNewType] = useState(TYPES[0].key);
  const create = useMutation({
    mutationFn: () => dashApi.createScenario({ title: newTitle, scenario_type: newType }),
    onSuccess: () => { setNewTitle(""); qc.invalidateQueries({ queryKey: ["scenarios"] }); qc.invalidateQueries({ queryKey: ["scenario-summary"] }); },
  });

  const toggleCompare = (id: number) =>
    setCompareIds((ids) => ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Scenario Planning & Executive Options"
        description="Compare transparent options BEFORE acting. Every scenario is manual assumptions plus a deterministic, formula-based calculation over a captured source baseline — never a source fact or a confirmed forecast. Actuals, forecasts, targets and scenarios stay separate."
      />

      {summary.data && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Active scenarios" value={summary.data.active_count} />
          <Stat label="Need an owner" value={summary.data.unowned_count} />
          <Stat label="On stale inputs" value={summary.data.stale_input_count} />
          <Stat label="Biggest sales variance" value={summary.data.biggest_sales_variance ? `${summary.data.biggest_sales_variance.variance >= 0 ? "+" : ""}${summary.data.biggest_sales_variance.variance}` : "—"} sub={summary.data.biggest_sales_variance?.title} />
        </div>
      )}

      {/* Executive Options — management-selected, not auto-chosen */}
      {summary.data && (summary.data.needs_decision.length > 0 || summary.data.unowned_count > 0) && (
        <Panel title="Executive Options" subtitle="IRONDash never chooses a business decision automatically. These options need management input.">
          {summary.data.needs_decision.length === 0 ? (
            <EmptyState compact>No scenarios currently need a decision.</EmptyState>
          ) : (
            <ul className="space-y-1 text-sm">
              {summary.data.needs_decision.map((n) => (
                <li key={n.id} className="text-slate-300">
                  <span className="text-amber-300">Decision needed:</span> {n.title}
                  <span className="ml-2 text-[11px] text-slate-500">(unowned — assign an owner and confirm assumptions)</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      )}

      <Panel title="Create a scenario" subtitle="Captures the current source baseline (read-only) and seeds neutral manual assumptions.">
        <div className="flex flex-wrap items-end gap-2">
          <input
            value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Scenario title"
            className="flex-1 rounded border border-slate-700 bg-slate-800 px-2 py-1.5 text-sm text-slate-200"
          />
          <select value={newType} onChange={(e) => setNewType(e.target.value)} className="rounded border border-slate-700 bg-slate-800 px-2 py-1.5 text-sm text-slate-200">
            {TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <button onClick={() => create.mutate()} disabled={create.isPending || !newTitle.trim()} className="rounded bg-sky-500/20 px-3 py-1.5 text-sm text-sky-200 disabled:opacity-40">Create draft</button>
        </div>
      </Panel>

      <Panel title="Scenarios" subtitle="Open a scenario to edit assumptions, see the deterministic output + formula, and manage its lifecycle.">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded border border-slate-700 bg-slate-800 px-2 py-1 text-xs text-slate-200">
            <option value="">Any status</option>
            {["draft", "active", "archived"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="rounded border border-slate-700 bg-slate-800 px-2 py-1 text-xs text-slate-200">
            <option value="">Any type</option>
            {TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          {compareIds.length >= 2 && <span className="text-xs text-sky-300">{compareIds.length} selected to compare ↓</span>}
        </div>
        {list.isLoading ? <LoadingState /> : list.isError ? <ErrorState error={list.error} /> : (
          (list.data?.scenarios.length ?? 0) === 0
            ? <EmptyState>No scenarios yet. Create one above.</EmptyState>
            : <div className="space-y-2">{list.data!.scenarios.map((s) => (
                <ScenarioRow key={s.id} scenario={s} inCompare={compareIds.includes(s.id)} onToggleCompare={() => toggleCompare(s.id)} />
              ))}</div>
        )}
      </Panel>

      {compareIds.length >= 2 && <ComparePanel ids={compareIds} />}
    </div>
  );
}

function ScenarioRow({ scenario, inCompare, onToggleCompare }: { scenario: Scenario; inCompare: boolean; onToggleCompare: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/30">
      <div className="flex flex-wrap items-center gap-2 px-3 py-2">
        <input type="checkbox" checked={inCompare} onChange={onToggleCompare} title="Add to compare" className="accent-sky-500" />
        <button onClick={() => setOpen((o) => !o)} className="flex flex-1 flex-wrap items-center gap-2 text-left">
          <StatusPill status={scenario.status} />
          <span className="text-sm text-slate-200">{scenario.title}</span>
          <span className="text-[11px] text-slate-500">{typeLabel(scenario.scenario_type)}</span>
          {scenario.owner ? <span className="text-[11px] text-slate-400">👤 {scenario.owner}</span> : <span className="text-[11px] text-amber-400">unowned</span>}
          <TrustLabel trust={scenario.base_trust} />
        </button>
      </div>
      {open && <ScenarioDetail scenario={scenario} />}
    </div>
  );
}

function ScenarioDetail({ scenario }: { scenario: Scenario }) {
  const qc = useQueryClient();
  const full = useQuery({ queryKey: ["scenario", scenario.id], queryFn: () => dashApi.scenario(scenario.id) });
  const outputs = useQuery({ queryKey: ["scenario-outputs", scenario.id], queryFn: () => dashApi.scenarioOutputs(scenario.id) });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["scenario", scenario.id] });
    qc.invalidateQueries({ queryKey: ["scenario-outputs", scenario.id] });
    qc.invalidateQueries({ queryKey: ["scenarios"] });
    qc.invalidateQueries({ queryKey: ["scenario-summary"] });
  };
  const [err, setErr] = useState<string | null>(null);
  const patch = useMutation({
    mutationFn: (body: Parameters<typeof dashApi.updateScenario>[1]) => dashApi.updateScenario(scenario.id, body),
    onSuccess: () => { setErr(null); invalidate(); },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : "update failed"),
  });
  const dup = useMutation({ mutationFn: () => dashApi.duplicateScenario(scenario.id), onSuccess: invalidate });

  return (
    <div className="space-y-3 border-t border-slate-800 px-3 py-3 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => patch.mutate({ status: "active" })} disabled={scenario.status === "active"} className="rounded bg-emerald-500/15 px-2 py-0.5 text-emerald-300 disabled:opacity-30">activate</button>
        <button onClick={() => dashApi.archiveScenario(scenario.id).then(invalidate)} disabled={scenario.status === "archived"} className="rounded bg-slate-700 px-2 py-0.5 text-slate-300 disabled:opacity-30">archive</button>
        <button onClick={() => patch.mutate({ refresh_baseline: true })} className="rounded bg-sky-500/15 px-2 py-0.5 text-sky-200">refresh baseline</button>
        <button onClick={() => dup.mutate()} className="rounded bg-slate-700 px-2 py-0.5 text-slate-300">duplicate</button>
      </div>
      {err && <p className="text-rose-300">{err}</p>}

      {full.data && (
        <div>
          <p className="mb-1 text-slate-500">Assumptions <span className="text-slate-600">(source-derived values are read-only; manual inputs are editable)</span></p>
          <div className="space-y-1">
            {full.data.assumptions.map((a) => (
              <AssumptionRow key={a.id} a={a} onSave={(v, isBool) => patch.mutate({ assumptions: [isBool ? { key: a.key, bool_value: v as boolean } : { key: a.key, proposed_value: v as number }] })} />
            ))}
          </div>
        </div>
      )}

      {outputs.isLoading ? <LoadingState /> : outputs.data && <OutputsBlock o={outputs.data} />}
    </div>
  );
}

function AssumptionRow({ a, onSave }: { a: ScenarioAssumption; onSave: (v: number | boolean, isBool: boolean) => void }) {
  const [val, setVal] = useState(a.proposed_value != null ? String(a.proposed_value) : "");
  if (!a.is_manual) {
    return (
      <div className="flex items-center gap-2">
        <span className="w-56 text-slate-400">{a.label}</span>
        <span className="text-slate-300">{a.baseline_value ?? "—"} {a.unit}</span>
        <span className="text-[10px] text-emerald-400/70">source-derived baseline</span>
      </div>
    );
  }
  if (a.assumption_type === "boolean") {
    return (
      <div className="flex items-center gap-2">
        <span className="w-56 text-slate-400">{a.label}</span>
        <button onClick={() => onSave(!a.bool_value, true)} className={`rounded px-2 py-0.5 ${a.bool_value ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-700 text-slate-400"}`}>{a.bool_value ? "assumed true" : "assumed false"}</button>
        <span className="text-[10px] text-amber-400/70">manual assumption</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <span className="w-56 text-slate-400">{a.label}</span>
      <input type="number" value={val} onChange={(e) => setVal(e.target.value)} className="w-28 rounded border border-slate-700 bg-slate-800 px-1.5 py-0.5 text-slate-200 [color-scheme:dark]" />
      <span className="text-slate-500">{a.unit}</span>
      {String(a.proposed_value ?? "") !== val && (
        <button onClick={() => onSave(Number(val), false)} className="rounded bg-sky-500/20 px-1.5 py-0.5 text-sky-200">save</button>
      )}
      <span className="text-[10px] text-amber-400/70">manual assumption {a.min_range != null && `(range ${a.min_range}…${a.max_range})`}</span>
    </div>
  );
}

function OutputsBlock({ o }: { o: ScenarioOutputs }) {
  const out = o.outputs;
  const entries = Object.entries(out).filter(([k]) => !["formula", "separation_warning", "hypothetical_warning"].includes(k));
  return (
    <div className="rounded border border-slate-800 bg-slate-900/40 p-2">
      <p className="mb-1 text-slate-500">Scenario output <span className="text-[10px] text-slate-600">— deterministic; not a confirmed forecast</span></p>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <tbody>
            {entries.map(([k, v]) => (
              <tr key={k} className="border-t border-slate-800/60">
                <td className="py-0.5 pr-3 text-slate-500">{k}</td>
                <td className="py-0.5 text-slate-200">{v === null ? "—" : String(v)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {typeof out.formula === "string" && <p className="mt-2 text-[10px] text-slate-500">Formula: <code className="text-slate-400">{out.formula}</code></p>}
      {typeof out.separation_warning === "string" && <p className="mt-1 text-[11px] text-amber-300">{out.separation_warning}</p>}
      {typeof out.hypothetical_warning === "string" && <p className="mt-1 text-[11px] text-amber-300">{out.hypothetical_warning}</p>}
      <ul className="mt-2 space-y-0.5">
        {o.caveats.map((c, i) => <li key={i} className="text-[10px] text-slate-500">• {c}</li>)}
      </ul>
    </div>
  );
}

function ComparePanel({ ids }: { ids: number[] }) {
  const cmp = useQuery({ queryKey: ["scenario-compare", ids], queryFn: () => dashApi.scenariosCompare(ids) });
  return (
    <Panel title="Compare scenarios" subtitle="Scenarios vs the actual/pacing baseline. Text labels — not colour alone.">
      {cmp.isLoading ? <LoadingState /> : cmp.isError ? <ErrorState error={cmp.error} /> : cmp.data && (
        <div className="space-y-3">
          <div className="rounded border border-slate-800 bg-slate-900/40 p-2 text-xs">
            <p className="text-slate-500">Actual / pacing baseline (source-derived)</p>
            <p className="text-slate-300">Sales MTD: {String(cmp.data.baseline_actual.sales_mtd ?? "—")} · Projected EOM: {String(cmp.data.baseline_actual.sales_projected_eom ?? "—")} · Target: {String(cmp.data.baseline_actual.sales_target ?? "—")} · Risk: {String(cmp.data.baseline_actual.sales_risk_band ?? "—")}</p>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {cmp.data.scenarios.map((s: ScenarioOutputs) => (
              <div key={s.scenario.id} className="rounded border border-slate-800 bg-slate-900/30 p-2 text-xs">
                <div className="mb-1 flex items-center gap-2">
                  <StatusPill status={s.scenario.status} />
                  <span className="text-slate-200">{s.scenario.title}</span>
                  <TrustLabel trust={s.base_trust} />
                </div>
                <OutputsBlock o={s} />
              </div>
            ))}
          </div>
          <p className="text-[10px] text-slate-600">Labels used: {cmp.data.labels.join(" · ")}</p>
        </div>
      )}
    </Panel>
  );
}
